import React, { useState, Fragment } from 'react';
import { Box, TextField, Button, Typography, FormControlLabel, Checkbox, Link, useMediaQuery, useTheme, MenuItem, Grid, Tabs, Tab, Tooltip, Chip, InputAdornment, IconButton } from '@mui/material';
import { UploadCloudIcon, CheckCircle2Icon, CopyIcon, ExternalLinkIcon, InfoIcon, KeyIcon, ShieldCheckIcon, ArrowLeftIcon, CheckIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
const stepLabels = ['Registration', 'KYC / KYB', 'Agreement', 'API Setup', 'Verification'];
export const PartnerOnboarding: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [kycType, setKycType] = useState<'business' | 'individual'>('business');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [formData, setFormData] = useState({
    partnerName: '',
    businessType: '',
    country: '',
    contactName: '',
    email: '',
    phone: '',
    altPhone: '',
    registrationNumber: '',
    countryId: '',
    businessAddress: ''
  });
  const handleNext = () => setActiveStep(prev => prev + 1);
  const handleBack = () => setActiveStep(prev => prev - 1);
  const getCountryIdTooltip = () => {
    switch (formData.country) {
      case 'Ghana':
        return 'Your TIN as registered with GRA';
      case 'Nigeria':
        return 'Your TIN or RC Number as registered with CAC/FIRS';
      case 'Kenya':
        return 'Your KRA PIN number';
      default:
        return 'Enter your local tax identification number e.g. TIN, VAT Reg No.';
    }
  };
  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '6px',
      backgroundColor: '#FFFFFF',
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: '#0052CC',
        borderWidth: 2
      }
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#D1D5DB'
    },
    '& .MuiInputLabel-root': {
      fontSize: '0.875rem',
      fontWeight: 500,
      color: '#374151',
      whiteSpace: 'nowrap',
      overflow: 'visible'
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: '#0052CC'
    },
    '& .MuiInputBase-input': {
      fontSize: '0.875rem',
      color: '#1F2937'
    }
  };
  const sectionLabelSx = {
    fontSize: '0.75rem',
    fontWeight: 700,
    color: '#6B7280',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.06em',
    mb: 2
  };
  // Custom Step Indicator — full labels, no truncation
  const renderStepper = () => <Box sx={{
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    mb: 5,
    position: 'relative'
  }} data-id="element-1733">
      {stepLabels.map((label, idx) => {
      const isCompleted = idx < activeStep;
      const isActive = idx === activeStep;
      return <Fragment key={label} data-id="element-1734">
            {/* Connecting line before circle (except first) */}
            {idx > 0 && <Box sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          mt: '16px'
        }} data-id="element-1735">
                <Box sx={{
            width: '100%',
            height: 2,
            backgroundColor: isCompleted ? '#0B7923' : '#E5E7EB'
          }} data-id="element-1736" />
              </Box>}
            <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          minWidth: 80
        }} data-id="element-1737">
              <Box sx={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: isCompleted ? '#0B7923' : isActive ? '#0052CC' : '#E5E7EB',
            color: isCompleted || isActive ? '#FFFFFF' : '#9CA3AF',
            fontSize: '0.8rem',
            fontWeight: 700,
            flexShrink: 0
          }} data-id="element-1738">
                {isCompleted ? <CheckIcon size={16} data-id="element-1739" /> : idx + 1}
              </Box>
              <Typography sx={{
            mt: 0.75,
            fontSize: '0.75rem',
            fontWeight: isActive ? 700 : 500,
            textAlign: 'center',
            color: isActive ? '#1F2937' : isCompleted ? '#0B7923' : '#9CA3AF',
            lineHeight: 1.2,
            whiteSpace: 'nowrap'
          }} data-id="element-1740">
                {label}
              </Typography>
            </Box>
          </Fragment>;
    })}
    </Box>;
  const renderStep1 = () => <Box data-id="element-1741">
      <Typography sx={sectionLabelSx} data-id="element-1742">Basic Information</Typography>
      <Grid container spacing={3} sx={{
      mb: 1
    }} data-id="element-1743">
        <Grid item xs={12} data-id="element-1744">
          <TextField label="Partner / Business Name" fullWidth size="small" required value={formData.partnerName} onChange={e => setFormData({
          ...formData,
          partnerName: e.target.value
        })} sx={fieldSx} data-id="element-1745" />
        </Grid>
        <Grid item xs={12} md={6} data-id="element-1746">
          <TextField select label="Business Type" fullWidth size="small" required value={formData.businessType} onChange={e => setFormData({
          ...formData,
          businessType: e.target.value
        })} sx={fieldSx} data-id="element-1747">
            <MenuItem value="PSP" data-id="element-1748">PSP</MenuItem>
            <MenuItem value="MNO" data-id="element-1749">MNO</MenuItem>
            <MenuItem value="IMTO" data-id="element-1750">IMTO</MenuItem>
            <MenuItem value="Bank" data-id="element-1751">Bank</MenuItem>
            <MenuItem value="Fintech" data-id="element-1752">Fintech</MenuItem>
            <MenuItem value="NGO" data-id="element-1753">NGO</MenuItem>
            <MenuItem value="DFI" data-id="element-1754">DFI</MenuItem>
          </TextField>
        </Grid>
        <Grid item xs={12} md={6} data-id="element-1755">
          <TextField select label="Country" fullWidth size="small" required value={formData.country} onChange={e => setFormData({
          ...formData,
          country: e.target.value
        })} sx={fieldSx} data-id="element-1756">
            <MenuItem value="Ghana" data-id="element-1757">🇬🇭 Ghana</MenuItem>
            <MenuItem value="Nigeria" data-id="element-1758">🇳🇬 Nigeria</MenuItem>
            <MenuItem value="Kenya" data-id="element-1759">🇰🇪 Kenya</MenuItem>
            <MenuItem value="South Africa" data-id="element-1760">🇿🇦 South Africa</MenuItem>
            <MenuItem value="Côte d'Ivoire" data-id="element-1761">🇨🇮 Côte d'Ivoire</MenuItem>
            <MenuItem value="Senegal" data-id="element-1762">🇸🇳 Senegal</MenuItem>
            <MenuItem value="Tanzania" data-id="element-1763">🇹🇿 Tanzania</MenuItem>
          </TextField>
        </Grid>
      </Grid>

      <Box sx={{
      borderTop: '1px solid #E5E7EB',
      mt: 4,
      pt: 3
    }} data-id="element-1764">
        <Typography sx={sectionLabelSx} data-id="element-1765">Contact Details</Typography>
      </Box>
      <Grid container spacing={3} sx={{
      mb: 1
    }} data-id="element-1766">
        <Grid item xs={12} sm={6} data-id="element-1767">
          <TextField label="Primary Contact Name" fullWidth size="small" required value={formData.contactName} onChange={e => setFormData({
          ...formData,
          contactName: e.target.value
        })} sx={fieldSx} data-id="element-1768" />
        </Grid>
        <Grid item xs={12} sm={6} data-id="element-1769">
          <TextField label="Email Address" type="email" fullWidth size="small" required value={formData.email} onChange={e => setFormData({
          ...formData,
          email: e.target.value
        })} sx={fieldSx} data-id="element-1770" />
        </Grid>
        <Grid item xs={12} sm={6} data-id="element-1771">
          <TextField label="Phone Number" type="tel" fullWidth size="small" required value={formData.phone} onChange={e => setFormData({
          ...formData,
          phone: e.target.value
        })} sx={fieldSx} data-id="element-1772" />
        </Grid>
        <Grid item xs={12} sm={6} data-id="element-1773">
          <TextField label="Alternate Phone" type="tel" fullWidth size="small" value={formData.altPhone} onChange={e => setFormData({
          ...formData,
          altPhone: e.target.value
        })} sx={fieldSx} data-id="element-1774" />
        </Grid>
      </Grid>

      <Box sx={{
      borderTop: '1px solid #E5E7EB',
      mt: 4,
      pt: 3
    }} data-id="element-1775">
        <Typography sx={sectionLabelSx} data-id="element-1776">Registration Details</Typography>
      </Box>
      <Grid container spacing={3} data-id="element-1777">
        <Grid item xs={12} sm={6} data-id="element-1778">
          <TextField label="Registration Number" fullWidth size="small" required value={formData.registrationNumber} onChange={e => setFormData({
          ...formData,
          registrationNumber: e.target.value
        })} sx={fieldSx} data-id="element-1779" />
        </Grid>
        <Grid item xs={12} sm={6} data-id="element-1780">
          <TextField label="Tax ID" fullWidth size="small" required value={formData.countryId} onChange={e => setFormData({
          ...formData,
          countryId: e.target.value
        })} sx={fieldSx} InputProps={{
          endAdornment: <InputAdornment position="end" data-id="element-1782">
                  <Tooltip title={getCountryIdTooltip()} arrow data-id="element-1783">
                    <IconButton size="small" edge="end" sx={{
                color: '#9CA3AF'
              }} data-id="element-1784">
                      <InfoIcon size={16} data-id="element-1785" />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
        }} data-id="element-1781" />
        </Grid>
        <Grid item xs={12} data-id="element-1786">
          <TextField label="Registered Business Address" fullWidth size="small" required value={formData.businessAddress} onChange={e => setFormData({
          ...formData,
          businessAddress: e.target.value
        })} sx={fieldSx} data-id="element-1787" />
        </Grid>
      </Grid>
    </Box>;
  const renderStep2 = () => <Box data-id="element-1788">
      <Typography sx={sectionLabelSx} data-id="element-1789">Compliance & Verification</Typography>
      <Box sx={{
      mb: 3,
      borderBottom: '1px solid #E5E7EB'
    }} data-id="element-1790">
        <Tabs value={kycType} onChange={(_, v) => setKycType(v)} sx={{
        '& .MuiTab-root': {
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.875rem'
        },
        '& .Mui-selected': {
          color: '#0052CC'
        },
        '& .MuiTabs-indicator': {
          backgroundColor: '#0052CC'
        }
      }} data-id="element-1791">
          <Tab label="Business (KYB)" value="business" data-id="element-1792" />
          <Tab label="Individual (KYC)" value="individual" data-id="element-1793" />
        </Tabs>
      </Box>
      {kycType === 'business' ? <Grid container spacing={3} data-id="element-1794">
          <Grid item xs={12} data-id="element-1795">
            <TextField label="Ownership Structure" fullWidth size="small" placeholder="e.g., LLC, Corporation, Partnership" sx={fieldSx} data-id="element-1796" />
          </Grid>
          <Grid item xs={12} data-id="element-1797">
            <TextField label="Business Activity Description" fullWidth multiline rows={3} size="small" sx={fieldSx} data-id="element-1798" />
          </Grid>
          <Grid item xs={12} data-id="element-1799">
            <TextField label="Primary Shareholder Information" fullWidth size="small" placeholder="Names of shareholders with >25% ownership" sx={fieldSx} data-id="element-1800" />
          </Grid>
        </Grid> : <Grid container spacing={3} data-id="element-1801">
          <Grid item xs={12} data-id="element-1802">
            <TextField label="Full Legal Name" fullWidth size="small" sx={fieldSx} data-id="element-1803" />
          </Grid>
          <Grid item xs={12} sm={6} data-id="element-1804">
            <TextField label="Date of Birth" type="date" fullWidth size="small" InputLabelProps={{
          shrink: true
        }} sx={fieldSx} data-id="element-1805" />
          </Grid>
          <Grid item xs={12} sm={6} data-id="element-1806">
            <TextField label="National ID / Passport Number" fullWidth size="small" sx={fieldSx} data-id="element-1807" />
          </Grid>
          <Grid item xs={12} data-id="element-1808">
            <TextField label="Residential Address" fullWidth size="small" sx={fieldSx} data-id="element-1809" />
          </Grid>
          <Grid item xs={12} data-id="element-1810">
            <TextField select label="Source of Funds" fullWidth size="small" sx={fieldSx} data-id="element-1811">
              <MenuItem value="business" data-id="element-1812">Business Revenue</MenuItem>
              <MenuItem value="investment" data-id="element-1813">Investment Capital</MenuItem>
              <MenuItem value="other" data-id="element-1814">Other</MenuItem>
            </TextField>
          </Grid>
        </Grid>}
      <Box sx={{
      borderTop: '1px solid #E5E7EB',
      mt: 4,
      pt: 3
    }} data-id="element-1815">
        <Typography sx={sectionLabelSx} data-id="element-1816">Upload Documents</Typography>
      </Box>
      <Box sx={{
      border: '2px dashed #D1D5DB',
      borderRadius: '8px',
      p: 4,
      textAlign: 'center',
      backgroundColor: '#FAFAFA',
      cursor: 'pointer',
      minHeight: 120,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      '&:hover': {
        borderColor: '#0052CC',
        backgroundColor: '#EFF6FF'
      }
    }} data-id="element-1817">
        <UploadCloudIcon size={32} color="#6B7280" style={{
        marginBottom: 8
      }} data-id="element-1818" />
        <Typography variant="body2" sx={{
        fontWeight: 600,
        color: '#1F2937'
      }} data-id="element-1819">
          Click to upload or drag and drop
        </Typography>
        <Typography variant="caption" sx={{
        color: '#6B7280',
        mt: 0.5
      }} data-id="element-1820">
          PNG, JPG or PDF (max. 10MB)
        </Typography>
      </Box>
      <Box sx={{
      mt: 3,
      p: 2,
      backgroundColor: '#EFF6FF',
      borderRadius: '6px',
      border: '1px solid #BFDBFE',
      display: 'flex',
      alignItems: 'center',
      gap: 1.5
    }} data-id="element-1821">
        <ShieldCheckIcon size={20} color="#0052CC" data-id="element-1822" />
        <Typography variant="body2" sx={{
        color: '#1E3A8A',
        fontWeight: 500
      }} data-id="element-1823">
          AML/KYC compliance checks will be triggered automatically upon
          submission.
        </Typography>
      </Box>
    </Box>;
  const renderStep3 = () => <Box data-id="element-1824">
      <Typography sx={sectionLabelSx} data-id="element-1825">Agreement & Terms</Typography>
      <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: 1.5,
      mb: 3
    }} data-id="element-1826">
        <Link href="#" sx={{
        color: '#0052CC',
        fontWeight: 600,
        fontSize: '0.875rem',
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        textDecoration: 'none',
        '&:hover': {
          textDecoration: 'underline'
        }
      }} data-id="element-1827">
          Ecobank Partner Terms of Service <ExternalLinkIcon size={14} data-id="element-1828" />
        </Link>
        <Link href="#" sx={{
        color: '#0052CC',
        fontWeight: 600,
        fontSize: '0.875rem',
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        textDecoration: 'none',
        '&:hover': {
          textDecoration: 'underline'
        }
      }} data-id="element-1829">
          Privacy Policy <ExternalLinkIcon size={14} data-id="element-1830" />
        </Link>
      </Box>
      <Box sx={{
      height: 200,
      overflowY: 'auto',
      border: '1px solid #E5E7EB',
      borderRadius: '8px',
      p: 2.5,
      backgroundColor: '#FAFAFA',
      mb: 3
    }} data-id="element-1831">
        <Typography variant="body2" sx={{
        color: '#4B5563',
        lineHeight: 1.7
      }} data-id="element-1832">
          <strong data-id="element-1833">1. Introduction</strong>
          <br data-id="element-1834" />
          These Terms of Service govern your access to and use of the Ecobank
          Partner Portal and associated APIs. By registering as a partner, you
          agree to comply with all applicable laws and regulations regarding
          cross-border remittances.
          <br data-id="element-1835" />
          <br data-id="element-1836" />
          <strong data-id="element-1837">2. Compliance and KYC/AML</strong>
          <br data-id="element-1838" />
          You are solely responsible for conducting appropriate Know Your
          Customer (KYC) and Anti-Money Laundering (AML) checks on your
          end-users in accordance with local regulations. Ecobank reserves the
          right to audit your compliance procedures.
          <br data-id="element-1839" />
          <br data-id="element-1840" />
          <strong data-id="element-1841">3. API Usage and Limits</strong>
          <br data-id="element-1842" />
          Access to the API is provided "as is". Rate limits apply based on your
          partner tier. You must not attempt to circumvent these limits or use
          the API for any unauthorized purpose.
          <br data-id="element-1843" />
          <br data-id="element-1844" />
          <strong data-id="element-1845">4. Settlement and Fees</strong>
          <br data-id="element-1846" />
          Settlement terms are defined in your specific commercial agreement.
          Standard processing fees apply to all successful transactions unless
          otherwise negotiated.
          <br data-id="element-1847" />
          <br data-id="element-1848" />
          <strong data-id="element-1849">5. Termination</strong>
          <br data-id="element-1850" />
          Ecobank may suspend or terminate your access immediately if we suspect
          fraudulent activity, compliance violations, or breach of these terms.
        </Typography>
      </Box>
      <FormControlLabel control={<Checkbox checked={agreedToTerms} onChange={e => setAgreedToTerms(e.target.checked)} sx={{
      '&.Mui-checked': {
        color: '#0052CC'
      }
    }} data-id="element-1852" />} label={<Typography variant="body2" sx={{
      fontWeight: 500,
      color: '#1F2937'
    }} data-id="element-1853">
            I have read and agree to the Terms of Service and Privacy Policy
          </Typography>} sx={{
      mt: 1
    }} data-id="element-1851" />
    </Box>;
  const renderStep4 = () => <Box data-id="element-1854">
      <Typography sx={sectionLabelSx} data-id="element-1855">API Keys</Typography>
      <Grid container spacing={3} sx={{
      mb: 1
    }} data-id="element-1856">
        <Grid item xs={12} sm={6} data-id="element-1857">
          <Box sx={{
          p: 2.5,
          border: '1px solid #E5E7EB',
          borderRadius: '8px',
          backgroundColor: '#FAFAFA',
          minHeight: 130
        }} data-id="element-1858">
            <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2
          }} data-id="element-1859">
              <Typography variant="body2" sx={{
              fontWeight: 600,
              color: '#1F2937',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }} data-id="element-1860">
                <KeyIcon size={16} color="#0052CC" data-id="element-1861" /> Production API Key
              </Typography>
              <Chip label="Live" size="small" sx={{
              height: 20,
              fontSize: '0.65rem',
              backgroundColor: '#ECFDF5',
              color: '#0B7923',
              fontWeight: 700
            }} data-id="element-1862" />
            </Box>
            <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }} data-id="element-1863">
              <TextField fullWidth size="small" value="pk_live_••••••••••••••••" disabled sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '6px',
                backgroundColor: '#fff',
                height: 36
              }
            }} data-id="element-1864" />
              <Button variant="outlined" size="small" sx={{
              minWidth: 80,
              height: 36,
              borderColor: '#D1D5DB',
              color: '#374151',
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '6px'
            }} data-id="element-1865">
                Generate
              </Button>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12} sm={6} data-id="element-1866">
          <Box sx={{
          p: 2.5,
          border: '1px solid #E5E7EB',
          borderRadius: '8px',
          backgroundColor: '#FAFAFA',
          minHeight: 130
        }} data-id="element-1867">
            <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2
          }} data-id="element-1868">
              <Typography variant="body2" sx={{
              fontWeight: 600,
              color: '#1F2937',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }} data-id="element-1869">
                <KeyIcon size={16} color="#6B7280" data-id="element-1870" /> Sandbox API Key
              </Typography>
              <Chip label="Test" size="small" sx={{
              height: 20,
              fontSize: '0.65rem',
              backgroundColor: '#F3F4F6',
              color: '#4B5563',
              fontWeight: 700
            }} data-id="element-1871" />
            </Box>
            <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }} data-id="element-1872">
              <TextField fullWidth size="small" value="pk_test_8f92a4b1c3d5e6f7" disabled sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '6px',
                backgroundColor: '#fff',
                height: 36
              }
            }} data-id="element-1873" />
              <Button variant="outlined" size="small" sx={{
              minWidth: 80,
              height: 36,
              borderColor: '#D1D5DB',
              color: '#374151',
              borderRadius: '6px'
            }} data-id="element-1874">
                <CopyIcon size={16} data-id="element-1875" />
              </Button>
            </Box>
          </Box>
        </Grid>
      </Grid>

      <Box sx={{
      borderTop: '1px solid #E5E7EB',
      mt: 4,
      pt: 3
    }} data-id="element-1876">
        <Typography sx={sectionLabelSx} data-id="element-1877">Sandbox Access</Typography>
      </Box>
      <Box sx={{
      p: 2.5,
      border: '1px solid #E5E7EB',
      borderRadius: '8px',
      backgroundColor: '#FAFAFA',
      mb: 1,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    }} data-id="element-1878">
        <Box data-id="element-1879">
          <Typography variant="body2" sx={{
          color: '#1F2937',
          fontWeight: 600,
          mb: 0.5
        }} data-id="element-1880">
            Sandbox Environment URL
          </Typography>
          <Typography variant="body2" sx={{
          color: '#0052CC',
          fontFamily: 'monospace',
          fontSize: '0.85rem'
        }} data-id="element-1881">
            https://sandbox.ecobank-openremit.com
          </Typography>
        </Box>
        <Chip label="Active" size="small" sx={{
        backgroundColor: '#ECFDF5',
        color: '#0B7923',
        fontWeight: 600,
        border: '1px solid #A7F3D0'
      }} data-id="element-1882" />
      </Box>

      <Box sx={{
      borderTop: '1px solid #E5E7EB',
      mt: 4,
      pt: 3
    }} data-id="element-1883">
        <Typography sx={sectionLabelSx} data-id="element-1884">Documentation & Resources</Typography>
      </Box>
      <Grid container spacing={3} data-id="element-1885">
        {['API Documentation', 'Integration Guide', 'Developer FAQs'].map((doc, idx) => <Grid item xs={12} sm={4} key={idx} data-id="element-1886">
              <Box sx={{
          p: 2,
          border: '1px solid #E5E7EB',
          borderRadius: '8px',
          backgroundColor: '#FAFAFA',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: 'pointer',
          minHeight: 56,
          '&:hover': {
            borderColor: '#0052CC',
            backgroundColor: '#EFF6FF'
          }
        }} data-id="element-1887">
                <Typography variant="body2" sx={{
            fontWeight: 500,
            color: '#1F2937'
          }} data-id="element-1888">
                  {doc}
                </Typography>
                <ExternalLinkIcon size={16} color="#6B7280" data-id="element-1889" />
              </Box>
            </Grid>)}
      </Grid>
    </Box>;
  const renderStep5 = () => <Box sx={{
    textAlign: 'center',
    py: 4
  }} data-id="element-1890">
      <CheckCircle2Icon size={64} color="#0052CC" style={{
      marginBottom: 16
    }} data-id="element-1891" />
      <Typography sx={{
      fontWeight: 700,
      fontSize: '1.25rem',
      color: '#1F2937',
      mb: 1.5
    }} data-id="element-1892">
        Application Under Review
      </Typography>
      <Typography variant="body2" sx={{
      color: '#4B5563',
      maxWidth: 420,
      mx: 'auto',
      mb: 5,
      lineHeight: 1.6
    }} data-id="element-1893">
        Your partner application has been submitted successfully. Our team will
        review your details within 2–3 business days.
      </Typography>
      <Box sx={{
      maxWidth: 380,
      mx: 'auto',
      textAlign: 'left',
      mb: 5,
      position: 'relative'
    }} data-id="element-1894">
        <Box sx={{
        position: 'absolute',
        left: 15,
        top: 32,
        bottom: 32,
        width: 2,
        backgroundColor: '#E5E7EB',
        zIndex: 0
      }} data-id="element-1895" />
        {[{
        label: 'Application Submitted',
        sub: 'Completed today',
        done: true,
        active: false
      }, {
        label: 'KYC / KYB Review',
        sub: 'In Progress',
        done: false,
        active: true
      }, {
        label: 'Final Approval',
        sub: 'Pending',
        done: false,
        active: false
      }].map((item, idx) => <Box key={idx} sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 2,
        mb: idx < 2 ? 3 : 0,
        position: 'relative',
        zIndex: 1
      }} data-id="element-1896">
            <Box sx={{
          width: 32,
          height: 32,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          backgroundColor: item.done ? '#0B7923' : item.active ? '#EFF6FF' : 'white',
          border: item.done ? 'none' : item.active ? '2px solid #0052CC' : '2px solid #D1D5DB'
        }} data-id="element-1897">
              {item.done ? <CheckIcon size={16} color="white" data-id="element-1898" /> : item.active ? <Box sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: '#0052CC'
          }} data-id="element-1899" /> : null}
            </Box>
            <Box sx={{
          pt: 0.5
        }} data-id="element-1900">
              <Typography variant="body2" sx={{
            fontWeight: 600,
            color: item.done || item.active ? '#1F2937' : '#6B7280'
          }} data-id="element-1901">
                {item.label}
              </Typography>
              <Typography variant="caption" sx={{
            color: item.active ? '#0052CC' : '#6B7280',
            fontWeight: item.active ? 600 : 400
          }} data-id="element-1902">
                {item.sub}
              </Typography>
            </Box>
          </Box>)}
      </Box>
      <Typography variant="body2" sx={{
      color: '#6B7280',
      mb: 4
    }} data-id="element-1903">
        You will receive a confirmation email at{' '}
        <strong data-id="element-1904">{formData.email || 'your registered email'}</strong> when
        approved.
      </Typography>
      <Box sx={{
      display: 'flex',
      justifyContent: 'center',
      gap: 1.5
    }} data-id="element-1905">
        <Button variant="outlined" onClick={() => navigate('/login')} sx={{
        borderRadius: '6px',
        textTransform: 'none',
        fontWeight: 600,
        borderColor: '#D1D5DB',
        color: '#374151',
        px: 3,
        py: '10px'
      }} data-id="element-1906">
          Back to Login
        </Button>
        <Tooltip title="Available after approval" data-id="element-1907">
          <span data-id="element-1908">
            <Button variant="contained" disabled sx={{
            borderRadius: '6px',
            textTransform: 'none',
            fontWeight: 600,
            px: 3,
            py: '10px'
          }} data-id="element-1909">
              Go to Dashboard
            </Button>
          </span>
        </Tooltip>
      </Box>
    </Box>;
  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return renderStep1();
      case 1:
        return renderStep2();
      case 2:
        return renderStep3();
      case 3:
        return renderStep4();
      case 4:
        return renderStep5();
      default:
        return null;
    }
  };
  return <Box sx={{
    display: 'flex',
    minHeight: '100vh'
  }} data-id="element-1910">
      {/* LEFT PANEL */}
      {!isMobile && <Box sx={{
      width: '42%',
      minWidth: 340,
      backgroundColor: '#0052CC',
      p: '48px',
      display: 'flex',
      flexDirection: 'column',
      color: 'white'
    }} data-id="element-1911">
          <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        mb: 8
      }} data-id="element-1912">
            <Box component="img" src="https://uploadthingy.s3.us-west-1.amazonaws.com/2TFKkyaUakWdHzgEBHAz75/openRemitLogo-1.png" alt="OpenRemit Logo" sx={{
          height: 32,
          filter: 'brightness(0) invert(1)'
        }} data-id="element-1913" />
            <Typography sx={{
          fontWeight: 700,
          fontSize: '1rem',
          color: 'white',
          borderLeft: '1px solid rgba(255,255,255,0.3)',
          pl: 1.5
        }} data-id="element-1914">
              Partner Portal
            </Typography>
          </Box>
          <Box sx={{
        mt: 'auto',
        mb: 'auto'
      }} data-id="element-1915">
            <Typography sx={{
          fontWeight: 700,
          fontSize: '2rem',
          lineHeight: 1.2,
          color: 'white',
          maxWidth: 380,
          mb: 2
        }} data-id="element-1916">
              Join the Ecobank Remittance Network
            </Typography>
            <Typography sx={{
          fontSize: '1rem',
          color: 'rgba(255,255,255,0.8)',
          maxWidth: 360,
          lineHeight: 1.5,
          mb: 5
        }} data-id="element-1917">
              Onboard your business in minutes. Access powerful APIs, manage
              multi-currency wallets, and scale your cross-border payments.
            </Typography>
            <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2.5
        }} data-id="element-1918">
              {[{
            title: 'Global Reach',
            desc: 'Connect to payout networks across 33 African countries.'
          }, {
            title: 'Real-time Settlement',
            desc: 'Instant processing and automated reconciliation.'
          }, {
            title: 'Bank-grade Security',
            desc: 'Enterprise compliance and fraud prevention built-in.'
          }].map((f, idx) => <Box key={idx} sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.5
          }} data-id="element-1919">
                  <Box sx={{
              width: 20,
              height: 20,
              borderRadius: '50%',
              border: '1.5px solid rgba(255,255,255,0.6)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              mt: 0.25
            }} data-id="element-1920">
                    <CheckIcon size={12} color="white" data-id="element-1921" />
                  </Box>
                  <Box data-id="element-1922">
                    <Typography sx={{
                fontWeight: 700,
                fontSize: '0.875rem',
                color: 'white'
              }} data-id="element-1923">
                      {f.title}
                    </Typography>
                    <Typography sx={{
                fontSize: '0.8125rem',
                color: 'rgba(255,255,255,0.7)',
                lineHeight: 1.4
              }} data-id="element-1924">
                      {f.desc}
                    </Typography>
                  </Box>
                </Box>)}
            </Box>
          </Box>
        </Box>}

      {/* RIGHT PANEL */}
      <Box sx={{
      flex: 1,
      backgroundColor: '#FFFFFF',
      display: 'flex',
      flexDirection: 'column',
      overflowY: 'auto',
      p: isMobile ? '24px' : '40px 48px'
    }} data-id="element-1925">
        {isMobile && <Box sx={{
        mb: 3,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5
      }} data-id="element-1926">
            <Box component="img" src="https://uploadthingy.s3.us-west-1.amazonaws.com/2TFKkyaUakWdHzgEBHAz75/openRemitLogo-1.png" alt="OpenRemit" sx={{
          height: 28
        }} data-id="element-1927" />
          </Box>}

        <Button startIcon={<ArrowLeftIcon size={14} data-id="element-1929" />} onClick={() => navigate('/login')} sx={{
        alignSelf: 'flex-start',
        textTransform: 'none',
        color: '#0052CC',
        fontWeight: 600,
        fontSize: '0.8125rem',
        mb: 4,
        p: 0,
        minWidth: 'auto',
        '&:hover': {
          backgroundColor: 'transparent',
          textDecoration: 'underline'
        }
      }} data-id="element-1928">
          Back to Login
        </Button>

        <Box sx={{
        maxWidth: 720,
        width: '100%',
        mx: 'auto',
        flex: 1,
        display: 'flex',
        flexDirection: 'column'
      }} data-id="element-1930">
          <Typography sx={{
          fontWeight: 700,
          fontSize: '1.5rem',
          color: '#1F2937',
          mb: 0.5
        }} data-id="element-1931">
            Partner Onboarding
          </Typography>
          <Typography sx={{
          fontSize: '0.875rem',
          color: '#6B7280',
          mb: 4
        }} data-id="element-1932">
            Complete the steps below to register your business.
          </Typography>

          {renderStepper()}

          {/* Form content — no gray box, directly on white */}
          <Box sx={{
          flex: 1
        }} data-id="element-1933">
            {renderStepContent()}

            {activeStep < 4 && <Box sx={{
            borderTop: '1px solid #E5E7EB',
            mt: 4,
            pt: 2.5,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }} data-id="element-1934">
                <Typography sx={{
              fontSize: '0.8125rem',
              color: '#6B7280',
              fontWeight: 500
            }} data-id="element-1935">
                  Step {activeStep + 1} of 5
                </Typography>
                <Box sx={{
              display: 'flex',
              gap: 1.5
            }} data-id="element-1936">
                  <Button variant="outlined" disabled={activeStep === 0} onClick={handleBack} sx={{
                borderRadius: '6px',
                textTransform: 'none',
                fontWeight: 600,
                borderColor: '#D1D5DB',
                color: '#374151',
                px: 3,
                py: '10px',
                minWidth: 100
              }} data-id="element-1937">
                    Back
                  </Button>
                  <Button variant="contained" onClick={handleNext} disabled={activeStep === 2 && !agreedToTerms} sx={{
                borderRadius: '6px',
                textTransform: 'none',
                fontWeight: 600,
                backgroundColor: '#0052CC',
                px: 3,
                py: '10px',
                minWidth: 140,
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: '#003DA8',
                  boxShadow: 'none'
                }
              }} data-id="element-1938">
                    {activeStep === 3 ? 'Submit Application' : 'Continue'}
                  </Button>
                </Box>
              </Box>}
          </Box>
        </Box>
      </Box>
    </Box>;
};
export default PartnerOnboarding;